import { allowOnlyNumeric, cls, NOOP, onDownloadFileFromServer } from '@/utils';
import { handlePrint } from '@/utils/pdf.util';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { InputRef } from 'antd';

import {
	DownloadButton,
	DownloadPersonalInfoButton,
	Input,
	Loading,
	PrintButton,
} from '..';
import {
	TDefineDocumentPreviewProps,
	TServerFile,
} from './define-document-preview.type';

interface PreviewFileProps {
	isLoading?: boolean;
	imageUrls: string[] | null;
	fileName?: string;
	fileList?: TDefineDocumentPreviewProps['fileList'];
}

const PreviewFile = ({
	isLoading,
	imageUrls,
	fileName,
	fileList,
}: PreviewFileProps) => {
	const [pageNumber, setPageNumber] = useState<number>(1);
	const [page, setPage] = useState<number>(1);
	const [fetching, setFetching] = useState<boolean>(false);
	const pageRefs = useRef<(HTMLDivElement | null)[]>([]);
	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<InputRef>(null);
	const isScrollingByButton = useRef(false);
	const scrollingTimer = useRef<NodeJS.Timeout | null>(null);
	const printIframeRef = useRef<HTMLIFrameElement | null>(null);
	const printUrlRef = useRef<string | null>(null);

	const handlePageChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setPage(Number(allowOnlyNumeric(e.target.value)));
		},
		[]
	);

	const onPressEnter = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (pageRefs.current[page - 1]) {
				isScrollingByButton.current = true;
				pageRefs.current[page - 1]?.scrollIntoView({ behavior: 'smooth' });

				scrollingTimer.current = setTimeout(() => {
					isScrollingByButton.current = false;
				}, 1000);
				inputRef.current?.blur();
			}
		},
		[pageRefs.current, page]
	);

	const handleScroll = useCallback(() => {
		if (isScrollingByButton.current) return;
		const container = containerRef.current;
		if (!container) return;

		const scrollPosition = container.scrollTop;
		let currentPage = 1;

		for (let i = 0; i < pageNumber; i++) {
			const pageElement = pageRefs.current[i];
			if (pageElement) {
				const { offsetTop, offsetHeight } = pageElement;
				if (scrollPosition >= offsetTop - offsetHeight / 2) {
					currentPage = i + 1;
				}
			}
		}

		setPage(currentPage);
	}, [
		isScrollingByButton.current,
		containerRef.current,
		pageRefs.current,
		pageNumber,
	]);

	useEffect(() => {
		setPageNumber(imageUrls?.length || 1);
	}, [imageUrls?.length]);

	useEffect(() => {
		const container = containerRef.current;
		if (container) {
			container.addEventListener('scroll', handleScroll);
			return () => {
				container.removeEventListener('scroll', handleScroll);
			};
		}
	}, [pageNumber]);

	useEffect(() => {
		return () => {
			clearTimeout(scrollingTimer.current!);

			// Clean up print resources when component unmounts
			if (printIframeRef.current) {
				document.body.removeChild(printIframeRef.current);
				printIframeRef.current = null;
			}
			if (printUrlRef.current) {
				URL.revokeObjectURL(printUrlRef.current);
				printUrlRef.current = null;
			}
		};
	}, []);

	const onPrint = useCallback(async () => {
		try {
			setFetching(true);

			await handlePrint({
				pageRefs,
				printIframeRef,
				printUrlRef,
				fileName,
			});
		} catch (error) {
			console.error('Error printing PDF:', error);
		} finally {
			setFetching(false);
		}
	}, [fileName]);

	const onDownload = useCallback(
		async (reason: string, callback?: () => void) => {
			if (!fileList?.[0]) return;
			try {
				callback?.();
				setFetching(true);

				const file = fileList?.[0]?.file as TServerFile;
				await onDownloadFileFromServer(file?.path, file?.name, {
					downloadReason: reason,
					downloadFileName: file?.name,
				});
			} catch (error) {
				console.error('Error downloading file:', error);
			} finally {
				setFetching(false);
			}
		},
		[fileList]
	);

	return (
		<div className="h-full">
			<Loading isLoading={isLoading || fetching} className="h-full" />

			<div className="relative mt-5 mb-3">
				<div className="flex justify-center items-center gap-2">
					<Input
						inputRef={inputRef}
						value={page}
						inputClassName="px-1.5"
						containerClassName="!w-10 !h-10 flex-none"
						maxLength={3}
						onChange={handlePageChange}
						onPressEnter={onPressEnter}
					/>
					/{' '}
					<span className="text-base font-normal text-font2">{pageNumber}</span>
				</div>

				<PrintButton onPrint={onPrint} className="right-20" />

				<DownloadPersonalInfoButton asChild onDownload={onDownload}>
					<DownloadButton onDownload={NOOP} />
				</DownloadPersonalInfoButton>
			</div>

			<div
				ref={containerRef}
				className="h-[calc(100vh_-_188px)] !overflow-auto"
			>
				{imageUrls?.map((imageUrl, index) => (
					<div
						key={index}
						className={cls(
							'flex justify-center',
							index !== pageNumber - 1 && 'border-gray1 border-b-[10px]'
						)}
						ref={(el) => (pageRefs.current[index] = el)}
					>
						<img
							src={imageUrl}
							alt="preview"
							className="max-w-full max-h-full"
						/>
					</div>
				))}
			</div>
		</div>
	);
};

export default memo(PreviewFile);
